// src/your/path/Restaurant.tsx

import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import { Mesa, Objeto, PedidoGroup } from '../../Configuracion/types';
import { Pedido } from '../types';
import SalonDisplayer from '../../Configuracion/Salones/components/SalonDisplayer/SalonDisplayer';
import MesaController from '../../Configuracion/Salones/components/SalonDisplayer/components/MesaController/MesaController';
import LocalClosedModal from '../Common/LocalClosed/LocalClosedModal';
import MasterMode from './MasterMode/MasterMode';
import ActionSection from './ActionSection/ActionSection';
import PedidosView from './PedidosView/PedidosView';
import PedidosListener from '../Common/PedidosListener/PedidosListener';
import './Restaurant.scss';
import MesasList from './MesasList/MesasList';
import { getActiveDeliveryPedidosByLocalId, getActiveCajaPedidosByLocalId, markPedidoAsDeliveredById, markPedidoAsCancelledById } from '../../../../crud/pedidos_api/pedidos';
import {isSessionOpenForOrders} from '../../../../crud/management_api/caja_session_records';
import { toast } from 'react-toastify';
import {
  useRestaurantData,
  useReservationsManager,
  useWaitlistManager,
  useResponsiveLayout,
  useDisplayPrefsManager,
  useHosterModeManager,
} from './hooks';
import { updateSalones } from '../../Configuracion/Salones/components/SalonDisplayer/components/functions/updateSalones';

const Restaurant: React.FC = () => {
  // Read URL search params for initial view
  const [searchParams] = useSearchParams();
  const initialView = searchParams.get('view') as 'mapa' | 'lista' | 'pedidos' | null;
  
  // Track MasterMode active tab state here to conditionally enable hooks
  const [masterModeTab, setMasterModeTab] = useState<'reservations' | 'waitlist' | 'sales' | 'caja'>('reservations');

  // Use custom hooks for all data management
  const {
    loading,
    salones,
    setSalones,
    selectedSalon,
    setSelectedSalon,
    showLocalClosedModal,
    redir,
    hosterMode,
    setHosterMode,
    displayPrefs,
    setDisplayPrefs,
  } = useRestaurantData();

  const {
    windowWidth,
    scaleFactor,
    setScaleFactor,
    viewMode,
    setViewMode,
  } = useResponsiveLayout();

  const {
    reservationsToday,
    setReservationsToday,
    filteredReservationsInAHour,
    setFilteredReservationsInAHour,
    fetchReservations,
  } = useReservationsManager({ hosterMode, salones, setSalones });

  const {
    waitList,
    setWaitList,
  } = useWaitlistManager({ 
    hosterMode, 
    enabled: masterModeTab === 'waitlist' // Only fetch when waitlist tab is active
  });

  const { handleSetHosterMode } = useHosterModeManager({ hosterMode, setHosterMode });
  const { updateDisplayPrefs } = useDisplayPrefsManager({ displayPrefs, setDisplayPrefs });

  // Session check state
  const [sessionOpenForOrders, setSessionOpenForOrders] = useState<boolean>(true);
  const [checkingSession, setCheckingSession] = useState<boolean>(true);

  // Local component state (UI-specific, not in hooks)
  const [selectedItem, setSelectedItem] = useState<Mesa | Objeto | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<PedidoGroup | null>(null);
  const [selectedPedido, setSelectedPedido] = useState<Pedido | null>(null);
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [loadingPedidos, setLoadingPedidos] = useState(false);
  const [showMesaModal, setShowMesaModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergedMesas, setMergedMesas] = useState<any[]>([]);
  const [showAllSalones, setShowAllSalones] = useState(false);
  const [viewPresentation, setViewPresentation] = useState<'mapa' | 'lista' | 'pedidos'>(initialView || 'mapa');
  const [contentView, setContentView] = useState<'mesas' | 'grupos'>('mesas'); // New state for Mesas/Grupos toggle

  // Compute selectedMesa and its salon name for the Sales component
  const selectedMesa = selectedItem && 'num' in selectedItem ? (selectedItem as Mesa) : null;
  const selectedMesaSalonName = useMemo(() => {
    if (!selectedMesa) return undefined;
    
    // Find the salon that contains this mesa
    const salon = salones.find(s => 
      s.mesas.some(m => m.id === selectedMesa.id)
    );
    
    return salon?.salon_name;
  }, [selectedMesa, salones]);

  // Check if session is open for orders on mount
  useEffect(() => {
    let mounted = true;
    
    // Safety timeout: Force checkingSession to false after 10 seconds
    const timeout = setTimeout(() => {
      if (mounted) {
        setCheckingSession(false);
        setSessionOpenForOrders(true);
      }
    }, 10000); // 10 seconds max for session check
    
    const checkSessionStatus = async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) {
          if (mounted) setCheckingSession(false);
          clearTimeout(timeout);
          return;
        }
        
        const user = JSON.parse(userStr);
        const localId = user?.local_id;

        if (!localId) {
          if (mounted) setCheckingSession(false);
          clearTimeout(timeout);
          return;
        }

        const response = await isSessionOpenForOrders(localId);
        const state = response?.data?.state ?? true; // Defensive access
        
        if (mounted) {
          setSessionOpenForOrders(state);
        }
      } catch (error) {
        console.error('Error checking session status:', error);
        // On error, assume session is open to avoid blocking unnecessarily
        if (mounted) {
          setSessionOpenForOrders(true);
        }
      } finally {
        clearTimeout(timeout);
        if (mounted) {
          setCheckingSession(false);
        }
      }
    };

    checkSessionStatus();
    
    return () => { 
      mounted = false;
      clearTimeout(timeout);
    };
  }, []);

  // Listen for receipt generation events to update mesa charged state in real-time
  useEffect(() => {
    const handleReceiptGenerated = (event: CustomEvent<{ mesa_id: number }>) => {
      const { mesa_id } = event.detail;
   
      setSalones((prevSalones: any) =>
        prevSalones.map((salon: any) => ({
          ...salon,
          mesas: salon.mesas.map((mesa: any) =>
            mesa.id === mesa_id ? { ...mesa, charged: true } : mesa
          ),
        }))
      );
      
      // Also update selectedItem if it's the same mesa
      if (selectedItem && 'id' in selectedItem && selectedItem.id === mesa_id) {
        setSelectedItem((prev: any) => prev ? { ...prev, charged: true } : prev);
      }
    };

    const handlePaymentCompleted = (event: CustomEvent<{ mesa_id: number }>) => {
      const { mesa_id } = event.detail;
      
      console.log('[RestaurantV2] Payment completed event received for mesa:', mesa_id);
      
      // Mark this mesa as recently payment-completed to prevent WebSocket overwrites
      (window as any).__recentlyPaymentCompleted = (window as any).__recentlyPaymentCompleted || new Map();
      (window as any).__recentlyPaymentCompleted.set(mesa_id, Date.now());
      
      // Clear the mesa used state when all receipts are paid
      setSalones((prevSalones: any) => {
        console.log('[RestaurantV2] Updating salones for payment completion, prev:', prevSalones);
        const updated = prevSalones.map((salon: any) => ({
          ...salon,
          mesas: salon.mesas.map((mesa: any) => {
            if (mesa.id === mesa_id) {
              console.log('[RestaurantV2] Found mesa to clear:', mesa.id, 'current state:', mesa.used, 'clearing to false');
              return { 
                ...mesa, 
                used: false,
                charged: false,
                user_id: null,
                user_name: null,
                time_used: null,
                time_last_order: null,
                total_charges: 0,
                current_people: 0,
              };
            }
            return mesa;
          }),
        }));
        console.log('[RestaurantV2] Updated salones after payment:', updated);
        
        // Verificar que la mesa realmente se actualizó
        const mesaUpdated = updated.flatMap((s: any) => s.mesas).find((m: any) => m.id === mesa_id);
        console.log('[RestaurantV2] Verification - Mesa after update:', mesa_id, 'used:', mesaUpdated?.used, 'charged:', mesaUpdated?.charged, 'full:', mesaUpdated);
        
        return updated;
      });
      
      // Also update selectedItem if it's the same mesa
      if (selectedItem && 'id' in selectedItem && selectedItem.id === mesa_id) {
        setSelectedItem((prev: any) => prev ? { 
          ...prev, 
          used: false,
          charged: false,
          user_id: null,
          user_name: null,
          time_used: null,
          time_last_order: null,
          total_charges: 0,
          current_people: 0,
        } : prev);
      }
    };

    window.addEventListener('mesa-receipt-generated', handleReceiptGenerated as EventListener);
    window.addEventListener('mesa-payment-completed', handlePaymentCompleted as EventListener);
    
    return () => {
      window.removeEventListener('mesa-receipt-generated', handleReceiptGenerated as EventListener);
      window.removeEventListener('mesa-payment-completed', handlePaymentCompleted as EventListener);
    };
  }, [selectedItem, setSalones]);

  // Keep selectedItem in sync with salones - when salones updates, update selectedItem with latest data
  // This ensures properties like 'charged' are reflected in selectedItem when salones is refreshed
  useEffect(() => {
    if (!selectedItem || !('id' in selectedItem) || !('num' in selectedItem)) return;
    
    const mesaId = (selectedItem as Mesa).id;
    
    // Find the mesa in salones with matching id
    for (const salon of salones) {
      const updatedMesa = salon.mesas.find((m: Mesa) => m.id === mesaId);
      if (updatedMesa) {
        // Only update if charged status differs (avoid infinite loop)
        if (updatedMesa.charged !== (selectedItem as Mesa).charged) {
          setSelectedItem(updatedMesa);
        }
        break;
      }
    }
  }, [salones, selectedItem]);

  // Handler for marking pedido as delivered
  const handleMarkAsDelivered = async (pedido_id: number) => {
    try {
      await markPedidoAsDeliveredById(pedido_id);
      setPedidos(prevPedidos => prevPedidos.filter(p => p.pedido_id !== pedido_id));
      toast.success('Pedido marcado como entregado');
      if (selectedPedido?.pedido_id === pedido_id) {
        setSelectedPedido(null);
      }
    } catch (error) {
      console.error('Error marking pedido as delivered:', error);
      toast.error('Error marcando pedido como entregado');
    }
  };

  // Handler for canceling pedido
  const handleCancelPedido = async (pedido_id: number) => {
    try {
      await markPedidoAsCancelledById(pedido_id);
      setPedidos(prevPedidos => prevPedidos.filter(p => p.pedido_id !== pedido_id));
      toast.success('Pedido cancelado');
      if (selectedPedido?.pedido_id === pedido_id) {
        setSelectedPedido(null);
      }
    } catch (error) {
      console.error('Error canceling pedido:', error);
      toast.error('Error cancelando pedido');
    }
  };

  // Function to refetch delivery/pickup pedidos (for real-time updates after payment)
  const refetchPedidos = async () => {
    if (viewPresentation !== 'pedidos') return;

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user.local_id;

    if (!localId) {
      console.error('No se pudo obtener el ID del local');
      return;
    }

    try {
      const [deliveryRes, pickupRes] = await Promise.all([
        getActiveDeliveryPedidosByLocalId(localId),
        getActiveCajaPedidosByLocalId(localId),
      ]);

      // Combine and filter only type 3 pedidos (both Delivery and Pickup/Caja use type 3)
      const allPedidos = [...(deliveryRes as any).data, ...(pickupRes as any).data]
        .filter(p => p.pedido_type_id === 3)
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      setPedidos(allPedidos);
      
      // Update selected pedido if it exists
      if (selectedPedido) {
        const updatedPedido = allPedidos.find(p => p.pedido_id === selectedPedido.pedido_id);
        if (updatedPedido) {
          setSelectedPedido(updatedPedido);
        }
      }
    } catch (error) {
      console.error('Error refetching pedidos:', error);
    }
  };

  // Fetch pedidos when switching to pedidos view
  useEffect(() => {
    if (viewPresentation !== 'pedidos') {
      setPedidos([]);
      setSelectedPedido(null);
      setLoadingPedidos(false);
      return;
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user.local_id;

    if (!localId) {
      toast.error('No se pudo obtener el ID del local');
      return;
    }

    setLoadingPedidos(true);

    // Fetch both delivery and pickup pedidos
    Promise.all([
      getActiveDeliveryPedidosByLocalId(localId),
      getActiveCajaPedidosByLocalId(localId),
    ])
      .then(([deliveryRes, pickupRes]) => {
        // Combine and filter only type 3 pedidos (both Delivery and Pickup/Caja use type 3)
        const allPedidos = [...(deliveryRes as any).data, ...(pickupRes as any).data]
          .filter(p => p.pedido_type_id === 3)
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        
        setPedidos(allPedidos);
      })
      .catch((error) => {
        console.error('Error fetching pedidos:', error);
        toast.error('Error cargando pedidos de delivery/pickup');
      })
      .finally(() => {
        setLoadingPedidos(false);
      });
  }, [viewPresentation]);

  // Clear mesa/group selection when switching to pedidos view
  useEffect(() => {
    if (viewPresentation === 'pedidos') {
      setSelectedItem(null);
      setSelectedGroup(null);
    }
  }, [viewPresentation]);

  // Ensure showAllSalones is always false when in pedidos view
  useEffect(() => {
    if (viewPresentation === 'pedidos' && showAllSalones) {
      setShowAllSalones(false);
    }
  }, [viewPresentation, showAllSalones]);

  // Auto-select virtual mesa when switching to map/lista view in hoster mode
  useEffect(() => {
    if (!hosterMode) return;
    if (viewPresentation === 'pedidos') return; // Skip when in pedidos view
    
    // Only auto-select if no mesa or group is currently selected
    if (selectedItem || selectedGroup) return;
    
    // Find the virtual mesa (MV) in any salon
    let virtualMesa: Mesa | null = null;
    
    for (const salon of salones) {
      const found = salon.mesas.find((mesa: Mesa) => mesa.is_virtual === true);
      if (found) {
        virtualMesa = found;
        break;
      }
    }
    
    // Auto-select the virtual mesa if found
    if (virtualMesa) {
      setSelectedItem(virtualMesa);
    }
  }, [viewPresentation, hosterMode, salones, selectedItem, selectedGroup]);

  // Handler para cuando se fija un salón desde ActionSection
  const handleSalonFixed = (fixedSalonData: { salon_id?: number; salon_name?: string; isVerTodos?: boolean }) => {
    // If "Ver Todos" is pinned, activate it
    if (fixedSalonData.isVerTodos) {
      setShowAllSalones(true);
      setSelectedSalon(undefined);
      return;
    }
    
    // Find the salon in the salones array and set it as selected
    const salonToSelect = salones.find((s: any) => 
      (fixedSalonData.salon_id != null && String(s.salon_id) === String(fixedSalonData.salon_id)) ||
      (fixedSalonData.salon_name && s.salon_name === fixedSalonData.salon_name)
    );
    
    if (salonToSelect) {
      setSelectedSalon(salonToSelect);
      setShowAllSalones(false);
    }
  };

  // Handler for mesa selection - automatically disables hoster mode on mobile
  const handleSelectMesa = (mesa: Mesa | Objeto | null) => {
    // Handle null case (deselection)
    if (mesa === null) {
      setSelectedItem(null);
      return;
    }
    
    // Only process Mesa objects (not Objeto - decorative items)
    if (!('num' in mesa)) {
      setSelectedItem(mesa);
      return;
    }
    
    // On mobile (<=1400px), disable hoster mode to allow MesaController to open normally
    if (windowWidth <= 1400 && hosterMode) {
      setHosterMode(false);
      toast.info('Modo Anfitrión desactivado para gestión de mesa');
    }
    
    setSelectedItem(mesa);
    setSelectedGroup(null);
  };

  // Handler for group selection - automatically disables hoster mode on mobile
  const handleSelectGroup = (group: PedidoGroup) => {
    // On mobile (<=1400px), disable hoster mode to allow MesaController to open normally
    if (windowWidth <= 1400 && hosterMode) {
      setHosterMode(false);
      toast.info('Modo Anfitrión desactivado para gestión de grupo');
    }
    
    setSelectedGroup(group);
    setSelectedItem(null);
  };

  // Open MesaController when in 'lista' view and a mesa is selected (mirror SalonDisplayer behavior)
  // Skip modal opening in hosterMode - the Sales panel will handle mesa interactions
  useEffect(() => {
    if (hosterMode) {
      setShowMesaModal(false);
      return;
    }
    
    if (viewPresentation === 'lista' && selectedItem && 'num' in selectedItem) {
      // Only open if mesa has state truthy (same as SalonDisplayer condition)
      if ((selectedItem as Mesa).state) setShowMesaModal(true);
    } else {
      setShowMesaModal(false);
    }
  }, [viewPresentation, selectedItem, hosterMode]);

  // Open MesaController when a group is selected in non-hosterMode
  // Skip modal opening in hosterMode - the Sales panel will handle group interactions
  useEffect(() => {
    if (hosterMode) {
      setShowGroupModal(false);
      return;
    }
    
    if (selectedGroup) {
      setShowGroupModal(true);
    } else {
      setShowGroupModal(false);
    }
  }, [selectedGroup, hosterMode]);

  // Adjust scaleFactor when toggling Ver Todos
  useEffect(() => {
    // Only adjust on desktop
    if (windowWidth <= 900) return;
    
    if (showAllSalones) {
      // Scale down to 0.65 when showing Ver Todos grid
      setScaleFactor(0.65);
    } else {
      // Scale back up to default desktop scale when viewing individual salon
      setScaleFactor(1.13);
    }
  }, [showAllSalones, windowWidth, setScaleFactor]);

  if (loading || checkingSession) return <LoadingSpinner text="Cargando Mesas" transparent />;

  // If session is closed for orders, show blocking message
  if (!sessionOpenForOrders) {
    return (
      <div className="pagina-salones-container">
        <div className="session-closed-container">
          <div className="session-closed-content">
            <div className="session-closed-icon">🔒</div>
            <h2 className="session-closed-title">Sesión Cerrada para Pedidos</h2>
            <p className="session-closed-message">
              Ya se hizo el arqueo de caja, no se pueden tomar más órdenes.
            </p>
            <p className="session-closed-instructions">
              <strong>Para continuar tomando pedidos:</strong>
            </p>
            <ul className="session-closed-list">
              <li>Inicia otro turno, o</li>
              <li>Termina la sesión actual</li>
            </ul>
            <p className="session-closed-note">
              Dirígete a la sección de <strong>Finanzas → Caja</strong> para administrar turnos y sesiones.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pagina-salones-container">
      <div className="restaurant-salones-container">
        <div className={`restaurant-salones-split-view-container ${hosterMode ? 'master-mode-active' : ''}`}>

          {/** SALÓN (always) **/}
          <div className={`salon-section ${hosterMode ? 'hoster-mode-active' : ''} ${showAllSalones && windowWidth > 1300 && viewPresentation !== 'pedidos' ? 'has-grid' : ''}`}>
            <ActionSection
              selectedSalon={selectedSalon}
              hosterMode={hosterMode}
              setHosterMode={handleSetHosterMode}
              salones={salones}
              onSelectSalon={(s) => { setShowAllSalones(false); setSelectedSalon(s); }}
              scaleFactor={scaleFactor}
              setScaleFactor={setScaleFactor}
              mergeMode={mergeMode}
              setMergeMode={setMergeMode}
              mergedMesas={mergedMesas}
              setMergedMesas={setMergedMesas}
              showAllSalones={showAllSalones}
              setShowAllSalones={setShowAllSalones}
              onSelectAllSalones={() => {
                if (window.innerWidth <= 1300) {
                  // Mobile: toggle showing all salones (keep current view presentation)
                  setShowAllSalones((v) => !v);
                } else {
                  // Desktop: toggle grid of all salones
                  setShowAllSalones((v) => !v);
                }
              }}
              viewPresentation={viewPresentation}
              setViewPresentation={setViewPresentation}
              onSalonFixed={handleSalonFixed}
              displayPrefs={displayPrefs}
              onUpdateDisplayPrefs={updateDisplayPrefs}
            />
            
            {/* Mesas/Grupos Selector - Only show in Mapa or Lista view, not in Pedidos view */}
            {viewPresentation !== 'pedidos' && (
              <div className="content-view-selector">
                <button
                  className={`content-view-btn ${contentView === 'mesas' ? 'active' : ''}`}
                  onClick={() => setContentView('mesas')}
                >
                  Mesas
                </button>
                <button
                  className={`content-view-btn ${contentView === 'grupos' ? 'active' : ''}`}
                  onClick={() => setContentView('grupos')}
                >
                  Grupos
                </button>
              </div>
            )}
            
            {/* Grid of all salones (desktop only) - Not shown in Pedidos view */}
            {showAllSalones && windowWidth > 1300 && viewPresentation !== 'pedidos' ? (
              contentView === 'mesas' ? (
                <div className="salones-grid">
                  {salones.map((s) => (
                    <div key={s.salon_id} className="salon-card">
                      <div className="salon-card__header">
                        <button
                          className="salon-card__enter"
                          onClick={() => {
                            setSelectedSalon(s);
                            setShowAllSalones(false);
                          }}
                        >
                          Entrar
                        </button>
                        <div className="salon-card__title">{s.salon_name}</div>
                      </div>
                      <div className="salon-card__body">
              {viewPresentation === 'mapa' ? (
                          <SalonDisplayer
                            salones={salones}
                            selectedSalon={s}
                            selectedItem={selectedItem}
                            setSelectedItem={handleSelectMesa}
                            setSelectedSalon={setSelectedSalon}
                            setSalones={setSalones}
                            isPedidos
                            actionType="restaurant"
                            setScaleFactor={setScaleFactor}
                            scaleFactor={scaleFactor}
                            mergeMode={mergeMode}
                            mergedMesas={mergedMesas}
                            setMergedMesas={setMergedMesas}
                            filteredReservationsInAHour={filteredReservationsInAHour}
                displayPrefs={displayPrefs}
                            hosterMode={hosterMode}
                          />
                        ) : (
                          <MesasList
                            salones={salones}
                            selectedSalon={s}
                displayPrefs={displayPrefs}
                            selectedGroup={selectedGroup}
                            onSelectMesa={handleSelectMesa}
                            onSelectGroup={handleSelectGroup}
                            hideGroups={true}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="global-groups-section">
                  <MesasList
                    salones={[]}
                    displayPrefs={displayPrefs}
                    selectedGroup={selectedGroup}
                    onSelectMesa={() => {}}
                    onSelectGroup={handleSelectGroup}
                    onlyGroups={true}
                  />
                </div>
              )
            ) : viewPresentation === 'pedidos' ? (
              <PedidosView
                pedidos={pedidos}
                onSelectPedido={(pedido) => {
                  setSelectedPedido(pedido);
                  setSelectedItem(null);
                }}
                selectedPedido={selectedPedido}
                onMarkAsDelivered={handleMarkAsDelivered}
                onCancel={handleCancelPedido}
                loading={loadingPedidos}
              />
            ) : contentView === 'mesas' ? (
              viewPresentation === 'mapa' ? (
                // Mapa view - Stack salons vertically on mobile when Ver Todos is active
                showAllSalones && windowWidth <= 1300 ? (
                  <div className="salones-stacked-mobile">
                    {salones.map((salon) => (
                      <div key={salon.salon_id} className="salon-stack-item">
                        <div className="salon-stack-header">
                          <h3 className="salon-stack-title">{salon.salon_name}</h3>
                          <button
                            className="salon-stack-enter"
                            onClick={() => {
                              setSelectedSalon(salon);
                              setShowAllSalones(false);
                            }}
                          >
                            Entrar
                          </button>
                        </div>
                        <div className="salon-stack-map">
                          <SalonDisplayer
                            salones={salones}
                            selectedSalon={salon}
                            selectedItem={selectedItem}
                            setSelectedItem={handleSelectMesa}
                            setSelectedSalon={setSelectedSalon}
                            setSalones={setSalones}
                            isPedidos
                            actionType="restaurant"
                            setScaleFactor={setScaleFactor}
                            scaleFactor={scaleFactor}
                            mergeMode={mergeMode}
                            mergedMesas={mergedMesas}
                            setMergedMesas={setMergedMesas}
                            filteredReservationsInAHour={filteredReservationsInAHour}
                            displayPrefs={displayPrefs}
                            hosterMode={hosterMode}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Regular mapa view (single salon or desktop grid)
                  <SalonDisplayer
                    salones={salones}
                    selectedSalon={showAllSalones ? undefined : selectedSalon}
                    selectedItem={selectedItem}
                    setSelectedItem={handleSelectMesa}
                    setSelectedSalon={setSelectedSalon}
                    setSalones={setSalones}
                    isPedidos
                    actionType="restaurant"
                    setScaleFactor={setScaleFactor}
                    scaleFactor={scaleFactor}
                    mergeMode={mergeMode}
                    mergedMesas={mergedMesas}
                    setMergedMesas={setMergedMesas}
                    filteredReservationsInAHour={filteredReservationsInAHour}
                    displayPrefs={displayPrefs}
                    hosterMode={hosterMode}
                  />
                )
              ) : (
                <MesasList
                  salones={salones}
                  selectedSalon={showAllSalones ? undefined : selectedSalon}
                  displayPrefs={displayPrefs}
                  selectedGroup={selectedGroup}
                  onSelectMesa={handleSelectMesa}
                  onSelectGroup={handleSelectGroup}
                  hideGroups={true}
                />
              )
            ) : (
              <div className="global-groups-section">
                <MesasList
                  salones={[]}
                  displayPrefs={displayPrefs}
                  selectedGroup={selectedGroup}
                  onSelectMesa={() => {}}
                  onSelectGroup={handleSelectGroup}
                  onlyGroups={true}
                />
              </div>
            )}
          </div>

          {/** INLINE MASTERMODE (>1300px) **/}
          {hosterMode && windowWidth > 1400 && (
            <div className={`hoster-section ${hosterMode ? 'hoster-mode-active' : ''}`}>
              <MasterMode 
                show={hosterMode}
                reservationsToday={reservationsToday}
                filteredReservationsInAHour={filteredReservationsInAHour}
                setReservationsToday={setReservationsToday}
                setFilteredReservationsInAHour={setFilteredReservationsInAHour}
                salones={salones}
                setSalones={setSalones}
                fetchReservations={hosterMode ? fetchReservations : undefined}
                waitList={waitList}
                setWaitList={setWaitList}
                selectedMesa={selectedMesa}
                selectedGroup={selectedGroup}
                salonName={selectedMesaSalonName}
                hosterMode={hosterMode}
                selectedPedido={selectedPedido}
                viewPresentation={viewPresentation}
                onClearPedidoSelection={() => setSelectedPedido(null)}
                onClearGroupSelection={() => setSelectedGroup(null)}
                onClearMesaSelection={() => setSelectedItem(null)}
                onRefetchPedidos={refetchPedidos}
                activeTab={masterModeTab}
                setActiveTab={setMasterModeTab}
                onMesaClosed={(mesaId) => {
                  console.log('[RestaurantV2] Mesa closed callback received for mesa:', mesaId);
                  updateSalones(mesaId, { event: 'table_closed', data: { mesa_id: mesaId } }, setSalones);
                }}
              />
            </div>
          )}

          {/** FLOATING BUTTONS & OVERLAYS (≤1400px) **/}
          {hosterMode && windowWidth <= 1400 && (
            <>
              {/* Reservaciones */}
              <button
                className="floating-reservaciones-btn"
                onClick={() =>
                  setViewMode(viewMode === 'salon' ? 'master' : 'salon')
                }
              >
                Panel
              </button>
              {viewMode === 'master' && (
                <div className="floating-master-container">
                  <button
                    className="back-to-salon-btn"
                    onClick={() => setViewMode('salon')}
                  >
                    Volver al Salón
                  </button>
                  <MasterMode 
                    show={viewMode === 'master'}
                    reservationsToday={reservationsToday}
                    filteredReservationsInAHour={filteredReservationsInAHour}
                    setReservationsToday={setReservationsToday}
                    setFilteredReservationsInAHour={setFilteredReservationsInAHour}
                    salones={salones}
                    setSalones={setSalones}
                    fetchReservations={viewMode === 'master' ? fetchReservations : undefined}
                    waitList={waitList}
                    setWaitList={setWaitList}
                    selectedMesa={selectedMesa}
                    salonName={selectedMesaSalonName}
                    hosterMode={hosterMode}
                    selectedPedido={selectedPedido}
                    viewPresentation={viewPresentation}
                    onClearPedidoSelection={() => setSelectedPedido(null)}
                    onRefetchPedidos={refetchPedidos}
                    activeTab={masterModeTab}
                    setActiveTab={setMasterModeTab}
                    onMesaClosed={(mesaId) => {
                      console.log('[RestaurantV2] Mesa closed callback received for mesa:', mesaId);
                      updateSalones(mesaId, { event: 'table_closed', data: { mesa_id: mesaId } }, setSalones);
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <LocalClosedModal show={showLocalClosedModal} redir={redir} />
      
      {/* PedidosListener for real-time table updates (mapa and lista views) */}
      {/* This listener handles table_opened/table_closed events to sync mesa state */}
      {viewPresentation !== 'pedidos' && (
        <PedidosListener 
          setPedidos={() => {}}
          pedido_type_id={1}
          updateSalones={(mesaId: number, payload: any) => {
            updateSalones(mesaId, payload, setSalones);
          }}
        />
      )}
      
      {/* PedidosListener for real-time updates when in pedidos view */}
      {/* Listen to type 3 which includes both Delivery and Pickup (Caja) pedidos */}
      {viewPresentation === 'pedidos' && (
        <PedidosListener 
          setPedidos={setPedidos}
          pedido_type_id={3}
        />
      )}
      
      {/* MesaController for mesa selection in lista view */}
      {viewPresentation === 'lista' && showMesaModal && selectedItem && 'num' in selectedItem && (selectedItem as Mesa).state && (
        <MesaController
          mesa={selectedItem as Mesa}
          show={showMesaModal}
          onClose={() => {
            setShowMesaModal(false);
            setSelectedItem(null);
          }}
          onMesaClosed={(mesaId) => {
            // Update salones state to reflect mesa closure in real-time
            updateSalones(mesaId, { event: 'table_closed', data: { mesa_id: mesaId } }, setSalones);
          }}
        />
      )}

      {/* MesaController for group selection (works in any view) */}
      {showGroupModal && selectedGroup && (
        <MesaController
          pedidoGroup={selectedGroup}
          show={showGroupModal}
          onClose={() => {
            setShowGroupModal(false);
            setSelectedGroup(null);
          }}
        />
      )}
    </div>
  );
};

export default Restaurant;
